import type { ChangesAlerts, ChangesMessages } from '../../types'
import type { GenericFcsPacket, MyWebcamServerData } from '../../types/mfc'
import {
    getRoomState,
    setRoomMessagesState,
    setRoomNewsMessage,
    setRoomTipState,
} from '../app-state/state.room'
import { AppStateType, SettingsType } from '../constants/enums'
import {
    calculateNewsSettings,
    showNewsInPrivateMessages,
    showsNewsInRooms,
} from '../mfcLogic/api/news'
import { initMyWebcam } from '../mfcLogic/videoPublisher'
import {
    PMSTATE,
    setPMBatchLoadingState,
    setPMChatExaustedState,
    setPMChatLoadingState,
    setPMChatNewsState,
    setPMChatTextState,
    setPMChatUniqueState,
    setPMChatUnreadState,
    setPMExaustedState,
    setPMLoadingState,
    setPMUnreadStateFragment,
} from '../state/state.pm'
import { safeDecodeUriComponent } from '../utilities/safeDecodeUriComponent'
import { openRuleViolationDialog } from './modals/state.dialog'
import { isQuickReplyOpen } from './modals/state.floater'
import { insertAlerts } from './state.alerts'
import { updateAppState, updateMyWebcamState } from './state.app'
import { MAILSTATE, emitMailboxUpdate } from './state.mail'
import { SETTINGSSTATE } from './state.settings'
import { USERSSTATE, slugToID } from './state.users'

export function onINBOX(payload: GenericFcsPacket) {
    const unreadCount = payload.arg1
    MAILSTATE.mail.unread = unreadCount
    emitMailboxUpdate(MAILSTATE)
    return
}

export function onPUBLISH(payload: { payload: MyWebcamServerData }) {
    const { payload: serverData } = payload
    updateAppState(AppStateType.MYWEBCAM, { serverData }, true)
    if (!serverData) return
    const { ctx, tkx, vidserv } = serverData
    const { id: userId, session: sessionId } = USERSSTATE[slugToID['me']]
    if (userId && sessionId && ctx && tkx && vidserv) {
        const eventDispatcher = updateMyWebcamState
        initMyWebcam({ sessionId, userId, ctx, tkx, vidserv, eventDispatcher })
    }
}

export const onALERTS = (data: { alerts: ChangesAlerts[] }) => {
    const { alerts } = data
    insertAlerts(alerts)
}

export const onRULEVIOLATION = (event: GenericFcsPacket) => {
    const message = safeDecodeUriComponent(event.payload as string)
    openRuleViolationDialog({ message })
}

export const onMessages = (data: { messages: ChangesMessages[] }) => {
    const { messages } = data
    messages.forEach((item) => {
        const {
            isExhausted,
            fragment,
            message,
            unread,
            news,
            show,
            done,
            pm,
            id,
        } = item

        if (message === null) {
            console.log(
                '[onMessages] NULL MESSAGE',
                JSON.stringify(message, null, 4),
            )
        }

        if (news) {
            const ueopt = SETTINGSSTATE[SettingsType.NEWS_NOTIFICATIONS]?.news
            const newsSettings = calculateNewsSettings(ueopt)
            const showNewsInPM = showNewsInPrivateMessages(
                newsSettings.FCNEWSOPT_IN_PM,
            )
            const showNewsInRoom = showsNewsInRooms(
                newsSettings.FCNEWSOPT_IN_CHAN,
            )

            if (showNewsInPM) {
                setPMChatNewsState(message)
            }

            if (showNewsInRoom) {
                setRoomNewsMessage(message)
            }

            return
        }

        if (typeof show === 'string') {
            if (show !== 'group') {
                setPMChatUniqueState(id, message, show + 'Show')
            }

            if (show !== 'club') {
                setRoomMessagesState(message, id)
            }

            return
        }

        if (!pm) {
            if (message?.tip) {
                const {
                    tipper: user,
                    timestamp,
                    amountInt,
                    images,
                    title,
                    model,
                    text,
                    note,
                    url,
                } = message

                setRoomTipState(
                    {
                        amount: amountInt,
                        displayed: false,
                        tipper: user,
                        timestamp,
                        images,
                        title,
                        model,
                        text,
                        note,
                        url,
                    },
                    id,
                )
            }

            setRoomMessagesState(message, id)
            return
        }

        if (message !== undefined) {
            if (message.basic) {
                setPMChatUniqueState(id, message, 'basic')
            } else {
                setPMChatTextState(id, message)
            }

            if (message.preview) {
                setPMLoadingState(false)
            }
        }

        if (unread !== undefined) {
            if (fragment) {
                setPMUnreadStateFragment(id, unread)
                return
            }
            setPMChatUnreadState(id, unread)
        }

        if (isExhausted !== undefined) {
            if (id) {
                setPMChatExaustedState(id, isExhausted)
                setPMChatLoadingState(id, false)
            } else {
                setPMExaustedState(isExhausted)
                setPMBatchLoadingState(false)
                setPMLoadingState(false)
            }
        }

        if (pm && done) {
            if (id) {
                setPMChatLoadingState(id, false)
            } else {
                setPMBatchLoadingState(false)
                setPMLoadingState(false)
            }
        }

        if (!message) {
            return
        }

        const { history, loading, preview, system } = message

        if (history || loading || system || preview) {
            return
        }

        const roomSettings = SETTINGSSTATE[SettingsType.ROOM]
        const inlinePM = roomSettings['inlinePM']
        const availableSpaceView = roomSettings['availableSpaceView']

        if (inlinePM === 3) {
            // send pm to all rooms
            Object.keys(getRoomState()).forEach((id) => {
                setRoomMessagesState(
                    { ...message, pmCommand: true },
                    Number(id),
                )
            })
        }

        if (inlinePM === 1) {
            // send pm only to the room of this model
            setRoomMessagesState({ ...message, pmCommand: true }, id)
        }

        if (inlinePM === 32) {
            // send pm only to the room of this model only if the PM command was
            // used (check the PMs for the model)
            const { wasPMCommandUsed } = PMSTATE.chats[id] || {}

            if (
                wasPMCommandUsed &&
                !isQuickReplyOpen(id, true) &&
                availableSpaceView !== 'pm'
            ) {
                setRoomMessagesState({ ...message, pmCommand: true }, id)
            }
        }
    })
}
